import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { IDespesa } from '@model/Despesa';
import { IComboEmpresa } from '@model/Empresa';
import { IComboDespesaStatus } from '@model/Despesa';
import { IDespesaRegistrarRecibo, useDespesaService } from '@services/DespesaService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { DespesaRegistrarReciboForm } from './DespesaRegistrarReciboForm';

export type DespesaRegistrarReciboModalProps = {
    modal: IModalWindow
    onDespesaRegistrada: (despesa: AxiosResponse<IDespesa>) => void
    empresaCombo: IComboEmpresa
    despesaStatusCombo: IComboDespesaStatus
}

export function DespesaRegistrarReciboModal ({ modal, onDespesaRegistrada, empresaCombo, despesaStatusCombo }: DespesaRegistrarReciboModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const form = useForm<IDespesaRegistrarRecibo>({
        empresaId: useField(),
        descricao: useField(),
        dataReferencia: useField(),
        valor: useField(),
        referencia: useField()
    })

    const validation = useValidation<IDespesaRegistrarRecibo>({
        empresaId: yup.number().required(),
        descricao: yup.string().required(),
        dataReferencia: yup.date().required(),
        valor: yup.number().required().moreThan(0),
        referencia: yup.string().required()
    });

    const despesaService = useDespesaService();

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            form.clearErrors();

            validation.validate(values)
                .then(() => {
                    loading.start();
                    despesaService.registrarRecibo(values)
                        .then(response => {
                            message.success("Recibo criado com sucesso");
                            modal.close();
                            onDespesaRegistrada(response);
                            window.open(`/DespesaRecibo/${response.data.despesaId}`);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [form, loading, message, modal, onDespesaRegistrada, despesaService, validation]);

    const handleEnter = React.useCallback(() => {
        form.reset();
    }, [form]);

    return (
        <ModalWindow
            modal={modal}
            disabled={loading.isLoading}
            size="md"
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle 
                title={`Registro de Novo Recibo`}
            />

            <ModalWindowContent>
                <DespesaRegistrarReciboForm 
                    form={form}
                    empresaCombo={empresaCombo}

                />
            </ModalWindowContent>

            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm}>Registrar Recibo</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}